import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../main/baseQueryWithReauth';
import type {
  CreateHookRequest,
  CreateTopicRequest,
  DeleteHookRequest,
  DeleteTopicRequest,
  GetTopicsParams,
  GetTopicsResponse,
  PartnerHook,
  PartnerTopic,
  UpdateHookRequest,
  UpdateTopicRequest,
} from './models';

const partnerTopics = `partner_topics`;

const topicsScopeId = (eventId?: number | null) => eventId ?? 'global';

const jsonHeaders = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

export const partnerTopicsApi = createApi({
  reducerPath: partnerTopics,
  baseQuery: baseQueryWithReauth,
  tagTypes: [partnerTopics],
  endpoints: (build) => ({
    getTopics: build.query<GetTopicsResponse, GetTopicsParams>({
      query: ({ event_id }) => ({
        url: '/partner/topics',
        method: 'GET',
        params: event_id ? { event_id } : undefined,
        headers: { Accept: 'application/json' },
      }),
      providesTags: (_result, _error, { event_id }) => [
        { type: partnerTopics, id: topicsScopeId(event_id) },
      ],
    }),
    createTopic: build.mutation<{ data: PartnerTopic }, CreateTopicRequest>({
      query: ({ scope_event_id: _scope, ...body }) => ({
        url: '/partner/topics',
        method: 'POST',
        body,
        headers: jsonHeaders,
      }),
      invalidatesTags: (_r, _e, { scope_event_id }) => [
        { type: partnerTopics, id: topicsScopeId(scope_event_id) },
      ],
    }),
    updateTopic: build.mutation<{ data: PartnerTopic }, UpdateTopicRequest>({
      query: ({ id, title }) => ({
        url: `/partner/topics/${id}`,
        method: 'PATCH',
        body: { title },
        headers: jsonHeaders,
      }),
      invalidatesTags: (_r, _e, { scope_event_id }) => [
        { type: partnerTopics, id: topicsScopeId(scope_event_id) },
      ],
    }),
    deleteTopic: build.mutation<{ data: PartnerTopic }, DeleteTopicRequest>({
      query: ({ id }) => ({
        url: `/partner/topics/${id}`,
        method: 'DELETE',
        headers: { Accept: 'application/json' },
      }),
      invalidatesTags: (_r, _e, { scope_event_id }) => [
        { type: partnerTopics, id: topicsScopeId(scope_event_id) },
      ],
    }),
    createHook: build.mutation<{ data: PartnerHook }, CreateHookRequest>({
      query: ({ topic_id, content }) => ({
        url: `/partner/topics/${topic_id}/hooks`,
        method: 'POST',
        body: { content },
        headers: jsonHeaders,
      }),
      invalidatesTags: (_r, _e, { scope_event_id }) => [
        { type: partnerTopics, id: topicsScopeId(scope_event_id) },
      ],
    }),
    updateHook: build.mutation<{ data: PartnerHook }, UpdateHookRequest>({
      query: ({ id, content }) => ({
        url: `/partner/hooks/${id}`,
        method: 'PATCH',
        body: { content },
        headers: jsonHeaders,
      }),
      invalidatesTags: (_r, _e, { scope_event_id }) => [
        { type: partnerTopics, id: topicsScopeId(scope_event_id) },
      ],
    }),
    deleteHook: build.mutation<{ data: PartnerHook }, DeleteHookRequest>({
      query: ({ id }) => ({
        url: `/partner/hooks/${id}`,
        method: 'DELETE',
        headers: { Accept: 'application/json' },
      }),
      invalidatesTags: (_r, _e, { scope_event_id }) => [
        { type: partnerTopics, id: topicsScopeId(scope_event_id) },
      ],
    }),
  }),
});

export const {
  useGetTopicsQuery,
  useCreateTopicMutation,
  useUpdateTopicMutation,
  useDeleteTopicMutation,
  useCreateHookMutation,
  useUpdateHookMutation,
  useDeleteHookMutation,
} = partnerTopicsApi;
